import Papa from "papaparse";

export interface ParsedProduct {
  name: string;
  type: "product" | "service" | "plan";
  description?: string;
  price?: number;
  sku?: string;
  stock?: number;
  colors?: string[];
  dimensions?: string;
  duration?: number;
  service_category?: string;
  plan_billing_type?: string;
  plan_features?: string[];
  plan_trial_days?: number;
  is_active: boolean;
  external_id?: string;
  image_url?: string;
  product_url?: string;
}

export interface ColumnMapping {
  csvColumn: string;
  dbField: string | null;
  autoDetected: boolean;
  confidence: "high" | "medium" | "low" | "none";
  samples: string[];
}

export interface ParseResult {
  headers: string[];
  rows: Record<string, string>[];
  mappings: ColumnMapping[];
}

export interface ValidationResult {
  isValid: boolean;
  data: ParsedProduct;
  errors: string[];
  fieldErrors: Record<string, string>;
  /** Original CSV row index, set by callers when tracking row order through filtering. */
  originalIndex?: number;
}

// Derived from the real Postgres column types in the products table.
// integer = int4 in DB; decimal = numeric in DB.
export const FIELD_TYPE_SCHEMA: Record<string, "string" | "integer" | "decimal" | "boolean" | "string[]"> = {
  name: "string",
  type: "string",
  description: "string",
  sku: "string",
  dimensions: "string",
  service_category: "string",
  external_id: "string",
  image_url: "string",
  product_url: "string",
  price: "decimal",
  stock: "integer",
  duration: "integer",
  is_active: "boolean",
  colors: "string[]",
  plan_billing_type: "string",
  plan_features: "string[]",
  plan_trial_days: "integer",
};

// Parses numeric values from CSV/ERP exports in Brazilian and international formats.
// Returns typed result with explicit error when value doesn't match the expected mode.
export interface ErpNumberResult {
  value: number | null;
  error: string | null;
}

export function parseErpNumber(raw: string, mode: "integer" | "decimal"): ErpNumberResult {
  if (!raw || raw.trim() === "") {
    return { value: null, error: null };
  }

  // Remove currency symbols, spaces, non-breaking spaces
  let normalized = raw
    .replace(/[rR]\$\s*/g, "")
    .replace(/[\s\u00A0]/g, "")
    .trim();

  if (normalized === "") {
    return { value: null, error: null };
  }

  // Detect Brazilian format: comma is decimal separator
  // e.g. "1.234,56" or "38,90"
  if (normalized.includes(",")) {
    // Remove dots (thousand separators) and replace comma with dot (decimal)
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  }

  // Remove any remaining non-numeric chars except dot and minus
  const cleaned = normalized.replace(/[^\d.\-]/g, "");

  if (cleaned === "" || cleaned === "." || cleaned === "-") {
    return { value: null, error: `Valor numérico inválido: "${raw.trim()}"` };
  }

  const parsed = parseFloat(cleaned);

  if (isNaN(parsed)) {
    return { value: null, error: `Valor numérico inválido: "${raw.trim()}"` };
  }

  // For integer mode: validate the final result is a whole number
  if (mode === "integer") {
    if (Math.floor(parsed) !== parsed) {
      return {
        value: null,
        error: `Esperado número inteiro, recebido "${raw.trim()}" (valor: ${parsed})`,
      };
    }
    return { value: parsed, error: null };
  }

  // Decimal mode: accept as-is
  return { value: parsed, error: null };
}

const COLUMN_ALIASES: Record<string, string[]> = {
  name: ["nome", "name", "product_name", "titulo", "title", "descricao_curta", "produto", "item"],
  type: ["tipo", "type", "categoria_tipo", "product_type"],
  description: ["descricao", "description", "descricao_completa", "details", "detalhes", "observacao", "obs"],
  price: ["preco", "price", "valor", "preco_venda", "unit_price", "preco_unitario", "valor_unitario", "vlr"],
  sku: ["sku", "codigo", "code", "ref", "referencia", "barcode", "codigo_barras", "ean", "gtin", "cod"],
  stock: ["estoque", "stock", "quantidade", "qty", "inventory", "qtd", "quant"],
  colors: ["cores", "colors", "cor", "variantes_cor", "color", "variacao"],
  dimensions: ["dimensoes", "dimensions", "medidas", "size", "tamanho", "dim"],
  duration: ["duracao", "duration", "tempo", "minutes", "minutos", "tempo_servico"],
  service_category: ["categoria_servico", "service_category", "categoria", "category", "cat"],
  plan_billing_type: ["tipo_cobranca", "cobranca", "billing_type", "recorrencia", "frequencia"],
  plan_features: ["recursos", "features", "beneficios", "funcionalidades"],
  plan_trial_days: ["dias_teste", "trial", "dias_trial", "periodo_teste", "trial_days"],
  is_active: ["ativo", "is_active", "active", "status", "situacao", "habilitado"],
  external_id: ["id_externo", "external_id", "id_erp", "codigo_erp", "id_sistema", "ref_externa"],
  image_url: ["imagem_url", "image_url", "foto", "image", "imagem", "url_imagem", "foto_url"],
  product_url: ["product_url", "link", "url", "link_produto", "url_produto", "website", "site"],
};

// Boolean aliases
const TRUE_VALUES = ["sim", "s", "yes", "y", "true", "1", "ativo", "active", "habilitado", "enabled"];
const FALSE_VALUES = ["nao", "não", "n", "no", "false", "0", "inativo", "inactive", "desabilitado", "disabled"];

import * as XLSX from "xlsx";

export async function parseCSV(file: File): Promise<ParseResult> {
  const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

  if (isExcel) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" }) as Record<string, string>[];
          
          if (json.length === 0) {
            resolve({ headers: [], rows: [], mappings: [] });
            return;
          }
          
          // Force all values to strings and trim keys
          const rows = json.map(row => {
            const newRow: Record<string, string> = {};
            for (const [k, v] of Object.entries(row)) {
              newRow[k.trim()] = String(v);
            }
            return newRow;
          });
          
          const headers = Object.keys(rows[0]);
          const mappings = detectColumnMappings(headers, rows);
          resolve({ headers, rows, mappings });
        } catch (err: any) {
          reject(new Error(`Erro ao processar Excel: ${err.message}`));
        }
      };
      reader.onerror = () => reject(new Error("Erro ao ler o arquivo Excel"));
      reader.readAsArrayBuffer(file);
    });
  }

  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      encoding: "UTF-8",
      complete: (results) => {
        const headers = results.meta.fields || [];
        const rows = results.data as Record<string, string>[];
        const mappings = detectColumnMappings(headers, rows);
        resolve({ headers, rows, mappings });
      },
      error: (error) => {
        reject(new Error(`Erro ao processar CSV: ${error.message}`));
      },
    });
  });
}

export function detectColumnMappings(headers: string[], rows: Record<string, string>[]): ColumnMapping[] {
  // 1. Generate schema signature based on the header order
  const schemaSignature = headers.join("|");
  const savedMappingsStr = localStorage.getItem(`csv_mapping_${schemaSignature}`);
  let savedMappings: Record<string, string | null> = {};
  
  if (savedMappingsStr) {
    try {
      savedMappings = JSON.parse(savedMappingsStr);
    } catch (e) {
      // Ignorar erro de parse
    }
  }

  return headers.map((header) => {
    // Extrai até 3 exemplos não vazios
    const samples = rows
      .map(row => row[header])
      .filter(val => val && val.trim() !== "")
      .slice(0, 3);

    const normalizedHeader = normalizeString(header);
    let matchedField: string | null = null;
    let confidence: "high" | "medium" | "low" | "none" = "none";

    // 2. Tenta recuperar do histórico (maior confiança de todas)
    if (savedMappingsStr && savedMappings.hasOwnProperty(header)) {
      matchedField = savedMappings[header];
      confidence = "high";
    } else {
      // 3. Tenta heurística por aliases
      for (const [dbField, aliases] of Object.entries(COLUMN_ALIASES)) {
        // Match exato
        if (aliases.some((alias) => normalizeString(alias) === normalizedHeader)) {
          matchedField = dbField;
          confidence = "high";
          break;
        }
        
        // Match parcial (e.g. "nome do produto" contém "nome")
        if (!matchedField && aliases.some((alias) => normalizedHeader.includes(normalizeString(alias)))) {
          matchedField = dbField;
          confidence = "medium";
        }
      }
    }

    // Penalidade na heurística baseada no conteúdo
    // Exemplo: se achou que é SKU, mas tem frase longa
    if (matchedField === "sku" && samples.some(s => s.length > 50 || s.includes(" "))) {
      confidence = "low";
    }
    // Exemplo: se achou que é Categoria, mas só tem números
    if (matchedField === "service_category" && samples.length > 0 && samples.every(s => /^\d+$/.test(s.trim()))) {
      confidence = "low";
    }

    return {
      csvColumn: header,
      dbField: matchedField,
      autoDetected: matchedField !== null,
      confidence,
      samples,
    };
  });
}

function normalizeString(str: string): string {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

export function parseBoolean(value: string): boolean {
  if (!value || value.trim() === "") return true; // Default: active
  const normalized = normalizeString(value);
  if (TRUE_VALUES.some((v) => normalizeString(v) === normalized)) return true;
  if (FALSE_VALUES.some((v) => normalizeString(v) === normalized)) return false;
  return true;
}

export function parseColors(value: string): string[] {
  if (!value || value.trim() === "") return [];
  return value
    .split(/[;,|\n]/)
    .map((c) => c.trim())
    .filter((c) => c.length > 0);
}

export function parseType(value: string): "product" | "service" | "plan" {
  if (!value || value.trim() === "") return "product";
  const normalized = normalizeString(value);
  const serviceAliases = ["service", "servico", "servicos", "s"];
  if (serviceAliases.some((v) => normalizeString(v) === normalized)) {
    return "service";
  }
  const planAliases = ["plan", "plano", "planos", "p"];
  if (planAliases.some((v) => normalizeString(v) === normalized)) {
    return "plan";
  }
  return "product";
}

const FIELD_LABELS: Record<string, string> = {
  name: "Nome",
  price: "Preço",
  stock: "Estoque",
  duration: "Duração",
  plan_trial_days: "Dias de Teste",
  is_active: "Ativo",
};

export function transformRow(
  row: Record<string, string>,
  mappings: ColumnMapping[]
): ValidationResult {
  const errors: string[] = [];
  const fieldErrors: Record<string, string> = {};
  const data: Partial<ParsedProduct> = {
    is_active: true,
    type: "product",
  };

  for (const mapping of mappings) {
    if (!mapping.dbField) continue;

    const value = row[mapping.csvColumn]?.trim() || "";
    const fieldType = FIELD_TYPE_SCHEMA[mapping.dbField] || "string";
    const fieldLabel = FIELD_LABELS[mapping.dbField] || mapping.dbField;

    switch (mapping.dbField) {
      case "name":
        if (value) data.name = value;
        break;

      case "type":
        data.type = parseType(value);
        break;

      case "description":
      case "sku":
      case "dimensions":
      case "service_category":
      case "plan_billing_type":
      case "external_id":
      case "image_url":
      case "product_url":
        if (value) (data as any)[mapping.dbField] = value;
        break;

      case "price": {
        if (!value) break;
        const priceResult = parseErpNumber(value, "decimal");
        if (priceResult.error) {
          fieldErrors[mapping.dbField] = priceResult.error;
          errors.push(`${fieldLabel}: ${priceResult.error}`);
        } else if (priceResult.value !== null) {
          data.price = priceResult.value;
        }
        break;
      }

      case "stock": {
        if (!value) break;
        const stockResult = parseErpNumber(value, "integer");
        if (stockResult.error) {
          fieldErrors[mapping.dbField] = stockResult.error;
          errors.push(`${fieldLabel}: ${stockResult.error}`);
        } else if (stockResult.value !== null) {
          data.stock = stockResult.value;
        }
        break;
      }

      case "duration": {
        if (!value) break;
        const durationResult = parseErpNumber(value, "integer");
        if (durationResult.error) {
          fieldErrors[mapping.dbField] = durationResult.error;
          errors.push(`${fieldLabel}: ${durationResult.error}`);
        } else if (durationResult.value !== null) {
          data.duration = durationResult.value;
        }
        break;
      }

      case "plan_trial_days": {
        if (!value) break;
        const trialResult = parseErpNumber(value, "integer");
        if (trialResult.error) {
          fieldErrors[mapping.dbField] = trialResult.error;
          errors.push(`${fieldLabel}: ${trialResult.error}`);
        } else if (trialResult.value !== null) {
          data.plan_trial_days = trialResult.value;
        }
        break;
      }

      case "colors":
        data.colors = parseColors(value);
        break;

      case "plan_features":
        data.plan_features = parseColors(value); // we can reuse parseColors as it just splits by standard delimiters
        break;

      case "is_active":
        data.is_active = parseBoolean(value);
        break;
    }
  }

  // Required field validation
  if (!data.name) {
    errors.push("Nome é obrigatório");
    fieldErrors["name"] = "Campo obrigatório";
  }

  return {
    isValid: errors.length === 0,
    data: data as ParsedProduct,
    errors,
    fieldErrors,
  };
}

export function getAvailableFields(): { value: string; label: string }[] {
  return [
    { value: "name", label: "Nome do produto (obrigatório)" },
    { value: "type", label: "Tipo (produto/serviço/plano)" },
    { value: "description", label: "Descrição longa" },
    { value: "price", label: "Preço de venda" },
    { value: "sku", label: "SKU / Código interno" },
    { value: "stock", label: "Estoque" },
    { value: "colors", label: "Cores" },
    { value: "dimensions", label: "Dimensões" },
    { value: "duration", label: "Duração (minutos)" },
    { value: "service_category", label: "Categoria do Serviço" },
    { value: "plan_billing_type", label: "Tipo de Cobrança do Plano (ex: monthly)" },
    { value: "plan_features", label: "Recursos do Plano" },
    { value: "plan_trial_days", label: "Dias de Teste do Plano" },
    { value: "is_active", label: "Ativo" },
    { value: "external_id", label: "ID Externo" },
    { value: "image_url", label: "URL da Imagem" },
    { value: "product_url", label: "Link do Produto" },
  ];
}

export function getTemplateData(type: "product" | "service" | "plan"): string[][] {
  const headers = [
    "nome", "tipo", "descricao", "preco", "sku", "estoque",
    "cores", "dimensoes", "duracao", "categoria_servico", "tipo_cobranca", "recursos", "dias_teste", "ativo",
  ];

  if (type === "product") {
    return [
      headers,
      [
        "Camiseta Algodão", "product", "Camiseta 100% algodão confortável", "89,90",
        "CAM-ALG-01", "100", "Preto; Branco", "M; G", "", "", "", "", "", "sim"
      ],
      [
        "Tênis Esportivo", "product", "Tênis para corrida leve", "299,90",
        "TEN-ESP-01", "50", "Azul; Vermelho", "39; 40; 41", "", "", "", "", "", "sim"
      ]
    ];
  }

  if (type === "service") {
    return [
      headers,
      [
        "Corte de Cabelo", "service", "Corte masculino tradicional", "50,00",
        "SERV-CORTE", "", "", "", "45", "barbearia", "", "", "", "sim"
      ],
      [
        "Consultoria Financeira", "service", "Consultoria online de 1 hora", "250,00",
        "SERV-CONS", "", "", "", "60", "consultoria", "", "", "", "sim"
      ]
    ];
  }

  if (type === "plan") {
    return [
      headers,
      [
        "Plano Mensal", "plan", "Acesso ilimitado por 30 dias", "99,90",
        "PLAN-MEN", "", "", "", "", "", "monthly", "Suporte 24h; Todos os cursos", "7", "sim"
      ],
      [
        "Plano Anual", "plan", "Acesso anual com desconto", "999,90",
        "PLAN-ANU", "", "", "", "", "", "yearly", "Mentoria exclusiva; Acesso Vitalício", "14", "sim"
      ]
    ];
  }

  return [headers];
}

export function generateTemplateCSV(type: "product" | "service" | "plan" = "product"): string {
  const rows = getTemplateData(type);
  return rows.map((row) => row.map((cell) => `"${cell}"`).join(";")).join("\n");
}
